import React from 'react';
import styled from 'styled-components';
import i18next from 'i18next';

// Translate with the English text as the key; falls back to the English text
// when i18n is not ready (the boundary can render before/without it).
const t = (text) => {
  try {
    return i18next.t(text) || text;
  } catch (e) {
    return text;
  }
};

// Errors thrown while loading a lazily imported page, typically because a new
// deploy replaced the content-hashed bundles the open tab still points at.
const CHUNK_ERROR = /Loading chunk|Loading CSS chunk|dynamically imported module|Importing a module script failed|ChunkLoadError/i;
export const isChunkLoadError = (error) =>
  Boolean(error) && CHUNK_ERROR.test(`${error.name || ''} ${error.message || ''}`);

const makeErrorId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`.toUpperCase();

const Wrap = styled.div`
  --eb-ink: #2b3550;
  --eb-muted: #5d6987;
  --eb-card: #ffffff;
  --eb-border: #d7e3f7;
  --eb-brand: #4f81bd;
  --eb-danger: #d64545;
  --eb-code: #f4f7fd;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  min-height: ${({ $page }) => ($page ? '60vh' : '100vh')};
  padding: 24px 16px;
  font-family: 'Poppins', system-ui, -apple-system, 'Segoe UI', sans-serif;
  color: var(--eb-ink);

  @media (prefers-color-scheme: dark) {
    --eb-ink: #e3e9f7;
    --eb-muted: #a3afcb;
    --eb-card: #172238;
    --eb-border: #2c3a57;
    --eb-brand: #6aa6ff;
    --eb-danger: #ff7a7a;
    --eb-code: #0f1626;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 560px;
  box-sizing: border-box;
  padding: 32px 28px;
  text-align: center;
  background: var(--eb-card);
  border: 1px solid var(--eb-border);
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(31, 52, 94, 0.1);
`;

const Badge = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $info }) => ($info ? 'var(--eb-brand)' : 'var(--eb-danger)')};
  background: ${({ $info }) =>
    $info ? 'rgba(79, 129, 189, 0.14)' : 'rgba(214, 69, 69, 0.12)'};
`;

const Title = styled.h2`
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 600;
  color: var(--eb-ink);
`;

const Text = styled.p`
  margin: 0 0 22px;
  font-size: 15px;
  line-height: 1.55;
  color: var(--eb-muted);
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const buttonBase = `
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  padding: 10px 18px;
  border-radius: 999px;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  line-height: 1.2;
  transition: filter 0.15s ease, background 0.15s ease;
  &:focus-visible { outline: 3px solid rgba(79, 129, 189, 0.5); outline-offset: 2px; }
`;

const Primary = styled.button`
  ${buttonBase}
  color: #fff;
  background: linear-gradient(135deg, #6aa6ff, #4f81bd);
  border: 1px solid transparent;
  &:hover { filter: brightness(1.06); }
`;

const Secondary = styled.button`
  ${buttonBase}
  color: var(--eb-brand);
  background: transparent;
  border: 1px solid var(--eb-border);
  &:hover { background: rgba(79, 129, 189, 0.1); }
`;

const LinkButton = styled.a`
  ${buttonBase}
  color: var(--eb-brand);
  background: transparent;
  border: 1px solid var(--eb-border);
  &:hover { background: rgba(79, 129, 189, 0.1); }
`;

const Details = styled.details`
  margin-top: 22px;
  text-align: left;
  font-size: 13px;
  color: var(--eb-muted);

  summary { cursor: pointer; text-align: center; user-select: none; }
`;

const Code = styled.pre`
  margin: 10px 0;
  padding: 12px;
  max-height: 220px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.5;
  color: var(--eb-ink);
  background: var(--eb-code);
  border: 1px solid var(--eb-border);
  border-radius: 8px;
`;

const WarningIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.5 15a9 9 0 1 1-2.1-9.4L23 10" />
  </svg>
);

/**
 * Catches render errors below it and shows a recoverable error screen instead
 * of a blank page.
 *
 *  - variant="app"  (default) full-screen last resort around the whole app.
 *  - variant="page" sits around the routed page only, so the header and
 *    navigation stay usable; pair it with `resetKey={pathname}` so navigating
 *    to another page clears the error automatically.
 *
 * The real error is always written to the console (also in production), with
 * a short error id the user can quote to support.
 */
export class ErrorBoundary extends React.Component {
  state = { error: null, errorInfo: null, errorId: null, copied: false };

  static getDerivedStateFromError(error) {
    return { error, errorId: makeErrorId(), copied: false };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary ${this.state.errorId}] ${error && error.message}`,
      error,
      errorInfo && errorInfo.componentStack,
    );
    this.setState({ errorInfo });
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.reset();
    }
  }

  reset = () => this.setState({ error: null, errorInfo: null, errorId: null, copied: false });

  reload = () => window.location.reload();

  detailsText = () => {
    const { error, errorInfo, errorId } = this.state;
    return [
      `Error ID: ${errorId}`,
      `Time: ${new Date().toISOString()}`,
      `Page: ${window.location.pathname}${window.location.search}`,
      `Browser: ${navigator.userAgent}`,
      '',
      `${(error && error.name) || 'Error'}: ${(error && error.message) || String(error)}`,
      errorInfo && errorInfo.componentStack ? `\nComponent stack:${errorInfo.componentStack}` : '',
    ].join('\n');
  };

  copyDetails = async () => {
    const text = this.detailsText();
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      const area = document.createElement('textarea');
      area.value = text;
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        /* nothing more we can do */
      }
      document.body.removeChild(area);
    }
    this.setState({ copied: true });
  };

  render() {
    const { error, errorId, copied } = this.state;
    if (!error) return this.props.children;

    const page = this.props.variant === 'page';

    if (isChunkLoadError(error)) {
      return (
        <Wrap $page={page}>
          <Card role="alert">
            <Badge $info><RefreshIcon /></Badge>
            <Title>{t('A new version is available')}</Title>
            <Text>
              {t('AdminCat was updated while this page was open. Reload to continue.')}
            </Text>
            <Actions>
              <Primary type="button" onClick={this.reload}>{t('Reload page')}</Primary>
            </Actions>
          </Card>
        </Wrap>
      );
    }

    return (
      <Wrap $page={page}>
        <Card role="alert">
          <Badge><WarningIcon /></Badge>
          <Title>{t('Something went wrong')}</Title>
          <Text>
            {t('We could not display this page. Your data is safe. You can try again, reload the page, or go back to the dashboard.')}
          </Text>
          <Actions>
            <Primary type="button" onClick={this.reset}>{t('Try again')}</Primary>
            <Secondary type="button" onClick={this.reload}>{t('Reload page')}</Secondary>
            <LinkButton href="/dashboard">{t('Go to dashboard')}</LinkButton>
          </Actions>
          <Details>
            <summary>{t('Technical details')}</summary>
            <Code>{this.detailsText()}</Code>
            <Actions>
              <Secondary type="button" onClick={this.copyDetails}>
                {copied ? t('Copied') : t('Copy details')}
              </Secondary>
            </Actions>
            <Text style={{ marginTop: 12, marginBottom: 0, textAlign: 'center' }}>
              {t('If the problem continues, send these details to support and quote error ID')}{' '}
              <strong>{errorId}</strong>.
            </Text>
          </Details>
        </Card>
      </Wrap>
    );
  }
}

export const withErrorBoundary = (WrappedComponent) => {
  const WithErrorBoundary = (props) => (
    <ErrorBoundary>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );
  WithErrorBoundary.displayName = `WithErrorBoundary(${getDisplayName(WrappedComponent)})`;
  return WithErrorBoundary;
};

function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}
